<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'

import { FILE_MESSAGE_TYPE } from '@/utils/embeds/ide'

import uploadService from '@/services/embeds/plugin/upload.service'
import inputFilesService from '@/services/embeds/plugin/input.files.service'

import { useIdeStore } from '@/stores/embeds/ide.store'
import { useEmbedConfigureStore, type IInputFiles } from '@/stores/embeds/embedConfigure.store'

const ideStore = useIdeStore()
const embedConfigStore = useEmbedConfigureStore()
const { isUploadInProgress, inputFiles, getInputFilesCanUpload } = storeToRefs(ideStore)
const { getOnEditMode, getEmbedConfig } = storeToRefs(embedConfigStore)

const httpLoading = ref<boolean>(false)
const inputKey = ref<number>(0)

const isFileMessage = computed(() => {
  return ideStore.isFileUploadMessages !== null
})
const fileMessageType = computed(() => {
  return ideStore.isFileUploadMessages?.type || FILE_MESSAGE_TYPE.INFO
})
const fileMessage = computed(() => {
  return ideStore.isFileUploadMessages?.message || ''
})
const showInputFiles = computed(() => {
  return inputFiles.value && inputFiles.value.length > 0
})
/**
 * Gets the download url for the input file
 * @param file The file name
 * @returns The url
 */
const canRemove = (file: IInputFiles) => {
  return getOnEditMode.value
    ? true
    : getEmbedConfig.value?.inputFiles?.findIndex((fileItem) => fileItem.path === file.path) === -1
}
/**
 * remove the file
 * @param file The file to remove
 */
const removeFile = async (file: IInputFiles) => {
  if (!canRemove(file)) return
  httpLoading.value = true
  await uploadService.removeFile(file).then(() => {
    inputFiles.value.slice().forEach((fileItem, index) => {
      if (fileItem.path === file.path) {
        inputFiles.value.splice(index, 1)
      }
    })
  })
  httpLoading.value = false
}
/**
 * Tries to upload the file. this is required for not reloading the page
 * @param event The event
 */
const tryUploadFile = async (event: Event) => {
  event.preventDefault()
  httpLoading.value = true
  const embedId = embedConfigStore?.getEmbed?.id
  await uploadService.tryUploadFile(event, embedId).then((newFile: void | IInputFiles) => {
    if (newFile) {
      inputFiles.value = [...inputFiles.value, newFile]

      inputKey.value += 1
    }
  })
  httpLoading.value = false
}
/**
 * Gets the download url for the input file
 * @param file The file name
 * @returns The url
 */
const getUrlInput = (file: string) => {
  return `/api/projectSync/downloadItem?projectKey=1001&name=/${encodeURI(file)}&type=input`
}
/**
 * cancel the upload
 */
const cancelUpload = () => {
  uploadService.cancelUpload()
}
/**
 * @description handle file input click
 * @param event The event
 */
const handleFileInputClick = (event: Event) => {
  event.stopPropagation()
}
onMounted(async () => {
  await new Promise((resolve) => setTimeout(resolve, 100))
  if (getOnEditMode.value === false) {
    await inputFilesService.initInputFiles()
    await inputFilesService.onSettingChange()
  }
  if (!useIdeStore().isInputFilesUpdated) await uploadService.UpdateInputFiles()
})
onBeforeUnmount(async () => {
  useIdeStore().setInputFilesUpdated(false)
  if (getOnEditMode.value === false) {
    await inputFilesService.destroyInputFiles()
  }
  await new Promise((resolve) => setTimeout(resolve, 100))
})
</script>

<template>
  <div class="flex h-full w-full flex-col">
    <div class="flex h-auto w-full flex-col">
      <div v-if="getInputFilesCanUpload" class="flex flex-col gap-4 pb-5 pt-2">
        <span class="font-semibold leading-6">Upload Files:</span>
        <div class="relative flex w-full flex-col gap-2">
          <div v-if="!isUploadInProgress" class="flex h-full w-full justify-center rounded-lg">
            <button
              :disabled="httpLoading"
              class="relative h-full w-full rounded-lg border-2 border-dashed px-5 py-10 text-center"
            >
              <p class="font-semibold">Drag your file(s) here or Browse to instantly upload them</p>
              <input
                ref="uploadInput"
                type="file"
                :key="inputKey"
                name="file"
                class="absolute left-0 top-0 h-full w-full cursor-pointer opacity-0"
                @change="tryUploadFile($event)"
                @drop.prevent="tryUploadFile($event)"
                @dragover.prevent
                @dragenter.prevent
                @dragleave.prevent
                @click="handleFileInputClick"
              />
            </button>
          </div>
          <div v-else class="flex h-full w-full justify-center rounded-lg">
            <button
              :disabled="!httpLoading"
              @click="cancelUpload()"
              class="relative h-full w-full rounded-lg border-2 border-dashed px-5 py-10 text-center"
            >
              <p class="text-sm font-semibold sm:text-lg">Uploading... Click to cancel</p>
            </button>
          </div>

          <p
            v-if="isFileMessage"
            :class="[
              'font-thin',
              {
                success: fileMessageType === FILE_MESSAGE_TYPE.SUCCESS,
                error: fileMessageType === FILE_MESSAGE_TYPE.ERROR
              }
            ]"
          >
            {{ fileMessage }}
          </p>
        </div>
      </div>
      <div class="flex h-full flex-col gap-4">
        <span class="font-semibold leading-6">Files Manager</span>
        <div class="flex h-full w-full justify-between gap-1 rounded-md border border-gray-400 p-5">
          <ul v-if="showInputFiles" class="flex h-full w-full flex-col gap-2">
            <li
              v-for="file in inputFiles"
              :key="file.path"
              class="flex items-baseline justify-between gap-5 py-1"
            >
              <div class="flex gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="currentColor"
                >
                  <path
                    d="M14 3.3252V7.3252C14 7.59041 14.1054 7.84477 14.2929 8.0323C14.4804 8.21984 14.7348 8.3252 15 8.3252H19M14 3.3252H7C6.46957 3.3252 5.96086 3.53591 5.58579 3.91098C5.21071 4.28605 5 4.79476 5 5.3252V19.3252C5 19.8556 5.21071 20.3643 5.58579 20.7394C5.96086 21.1145 6.46957 21.3252 7 21.3252H17C17.5304 21.3252 18.0391 21.1145 18.4142 20.7394C18.7893 20.3643 19 19.8556 19 19.3252V8.3252M14 3.3252L19 8.3252M8 15.3252H16M11 11.3252V18.3252M8 11.3252H16V18.3252H8V11.3252Z"
                    stroke="#0A2540"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <p class="truncate font-semibold leading-6">/uploads/{{ file.path }}</p>
              </div>
              <div class="flex w-fit items-center justify-between gap-5">
                <a :href="getUrlInput(file.path)" target="_blank">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="20"
                    width="20"
                    viewBox="0 0 512 512"
                    fill="currentColor"
                  >
                    <path
                      d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32V274.7l-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7V32zM64 352c-35.3 0-64 28.7-64 64v32c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V416c0-35.3-28.7-64-64-64H346.5l-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352H64zm368 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48z"
                    />
                  </svg>
                </a>
                <button
                  @click="removeFile(file)"
                  :class="[
                    {
                      'cursor-not-allowed': !canRemove(file)
                    }
                  ]"
                >
                  <svg
                    v-if="canRemove(file)"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M20.0008 6C20.2557 6.00028 20.5009 6.09788 20.6862 6.27285C20.8715 6.44782 20.9831 6.68695 20.998 6.94139C21.013 7.19584 20.9302 7.44638 20.7666 7.64183C20.603 7.83729 20.3709 7.9629 20.1178 7.993L20.0008 8H19.9198L19.0008 19C19.0009 19.7652 18.7085 20.5015 18.1836 21.0583C17.6586 21.615 16.9407 21.9501 16.1768 21.995L16.0008 22H8.00085C6.40285 22 5.09685 20.751 5.00885 19.25L5.00384 19.083L4.08085 8H4.00085C3.74597 7.99972 3.50081 7.90212 3.31548 7.72715C3.13014 7.55218 3.01861 7.31305 3.00367 7.05861C2.98874 6.80416 3.07152 6.55362 3.23511 6.35817C3.3987 6.16271 3.63075 6.0371 3.88385 6.007L4.00085 6H20.0008Z"
                      fill="#0A2540"
                    />
                    <path
                      d="M14.0009 2C14.5313 2 15.04 2.21071 15.4151 2.58579C15.7901 2.96086 16.0009 3.46957 16.0009 4C16.0006 4.25488 15.903 4.50003 15.728 4.68537C15.553 4.8707 15.3139 4.98223 15.0595 4.99717C14.805 5.01211 14.5545 4.92933 14.359 4.76574C14.1636 4.60214 14.038 4.3701 14.0079 4.117L14.0009 4H10.0009L9.99385 4.117C9.96375 4.3701 9.83814 4.60214 9.64269 4.76574C9.44723 4.92933 9.19669 5.01211 8.94225 4.99717C8.68781 4.98223 8.44867 4.8707 8.2737 4.68537C8.09873 4.50003 8.00114 4.25488 8.00085 4C8.00069 3.49542 8.19126 3.00943 8.53435 2.63945C8.87744 2.26947 9.3477 2.04284 9.85085 2.005L10.0009 2H14.0009Z"
                      fill="#0A2540"
                    />
                    <path
                      d="M20.0008 6C20.2557 6.00028 20.5009 6.09788 20.6862 6.27285C20.8715 6.44782 20.9831 6.68695 20.998 6.94139C21.013 7.19584 20.9302 7.44638 20.7666 7.64183C20.603 7.83729 20.3709 7.9629 20.1178 7.993L20.0008 8H19.9198L19.0008 19C19.0009 19.7652 18.7085 20.5015 18.1836 21.0583C17.6586 21.615 16.9407 21.9501 16.1768 21.995L16.0008 22H8.00085C6.40285 22 5.09685 20.751 5.00885 19.25L5.00384 19.083L4.08085 8H4.00085C3.74597 7.99972 3.50081 7.90212 3.31548 7.72715C3.13014 7.55218 3.01861 7.31305 3.00367 7.05861C2.98874 6.80416 3.07152 6.55362 3.23511 6.35817C3.3987 6.16271 3.63075 6.0371 3.88385 6.007L4.00085 6H20.0008Z"
                      stroke="black"
                    />
                    <path
                      d="M14.0009 2C14.5313 2 15.04 2.21071 15.4151 2.58579C15.7901 2.96086 16.0009 3.46957 16.0009 4C16.0006 4.25488 15.903 4.50003 15.728 4.68537C15.553 4.8707 15.3139 4.98223 15.0595 4.99717C14.805 5.01211 14.5545 4.92933 14.359 4.76574C14.1636 4.60214 14.038 4.3701 14.0079 4.117L14.0009 4H10.0009L9.99385 4.117C9.96375 4.3701 9.83814 4.60214 9.64269 4.76574C9.44723 4.92933 9.19669 5.01211 8.94225 4.99717C8.68781 4.98223 8.44867 4.8707 8.2737 4.68537C8.09873 4.50003 8.00114 4.25488 8.00085 4C8.00069 3.49542 8.19126 3.00943 8.53435 2.63945C8.87744 2.26947 9.3477 2.04284 9.85085 2.005L10.0009 2H14.0009Z"
                      stroke="black"
                    />
                  </svg>
                  <svg
                    v-if="!canRemove(file)"
                    xmlns="http://www.w3.org/2000/svg"
                    height="24"
                    width="24"
                    viewBox="0 0 512 512"
                    fill="currentColor"
                  >
                    <path
                      d="M367.2 412.5L99.5 144.8C77.1 176.1 64 214.5 64 256c0 106 86 192 192 192c41.5 0 79.9-13.1 111.2-35.5zm45.3-45.3C434.9 335.9 448 297.5 448 256c0-106-86-192-192-192c-41.5 0-79.9 13.1-111.2 35.5L412.5 367.2zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256z"
                    />
                  </svg>
                </button>
              </div>
            </li>
          </ul>
          <p v-else class="font-thin">No files uploaded</p>
        </div>
      </div>
    </div>
  </div>
</template>
